import axios from 'axios';
import i18n from 'i18next';
import moment from 'moment-timezone';
import CryptoJS from 'crypto-js';
import i18nNation from 'i18n-nationality';

import { DEFAULT_DOCTOR_RATE, PAY_DOCTOR_RATE, SECRET_KEY } from '../configs';

i18nNation.registerLocale(require('i18n-nationality/langs/en.json'));
i18nNation.registerLocale(require('../locales/th/nationalities.json'));

const showCallNow = true;

export const getPatientImageBase64 = patCode => new Promise((resolve, reject) => {
  axios.get(`files/base64/byPatCode/${patCode}`).then(response => {
    resolve((response?.data?.fileBase64 !== 'null' && response?.data?.fileBase64) || '');
  }).catch(() => {
    reject();
  });
});

export const getDoctorProfileImageBase64 = docCode => new Promise((resolve, reject) => {
  axios.get(`files/base64/byDocCode/${docCode}`).then(response => {
    resolve((response?.data?.fileBase64 !== 'null' && response?.data?.fileBase64) || '');
  }).catch(() => {
    reject();
  });
});

export const getFileUrl = file => new Promise((resolve, reject) => {
  axios.post('files/url', file).then(response => {
    resolve((response?.data?.fileUrl !== 'null' && response?.data?.fileUrl) || '');
  }).catch(() => {
    reject();
  });
});

export const checkFileUrl = async path => {
  if (!path) {
    return false;
  }

  try {
    const response = await fetch(path);
    return response.ok;
  } catch {
    return false;
  }
};

export const getFileUrlBaes64 = file => new Promise((resolve, reject) => {
  axios.post('files/base64', file).then(response => {
    resolve((response?.data?.fileBase64 !== 'null' && response?.data?.fileBase64) || '');
  }).catch(() => {
    reject();
  });
});

export const uploadFileBase64 = file => new Promise((resolve, reject) => {
  axios.post('files/upload', { file }).then(response => {
    resolve((response?.data?.data !== 'null' && response?.data?.data) || (response?.data !== 'null' && response?.data) || {});
  }).catch(() => {
    reject();
  });
});

export const getAgeByBirthDate = datetime => {
  if (!datetime || datetime === '' || datetime === 'null' || !moment(new Date(datetime.toString())).isValid()) {
    return { years: 0, months: 0 };
  }
  const years = moment().diff(moment(datetime), 'years');
  const months = moment().diff(moment(datetime), 'months') - (years * 12);
  return { years, months };
};

export const getAges = (datetime, t) => {
  const { years, months } = getAgeByBirthDate(datetime);
  return `${years} ${t('years')} ${months} ${t('months')}`;
};

export const getDatetimeLocal = (datetime, format = 'D MMMM YYYY HH:mm', useUTC = false, isConverYearTh = true) => {
  if (!datetime || datetime === '' || datetime === 'null' || !moment(new Date(datetime.toString())).isValid()) {
    return '-';
  }
  const timezone = localStorage.getItem('timezone') || moment.tz.guess();
  const result = useUTC ? moment.utc(datetime).tz(timezone) : moment(datetime);
  if (i18n.language === 'th' && isConverYearTh) {
    const convertYear = parseInt(result.format('YYYY')) + 543;
    return result.format(format).replace(result.format('YYYY'), convertYear);
  }
  return result.format(format);
};

export const getDatetimeFromZero = (info, format = 'D MMMM YYYY HH:mm', isConverYearTh = true) => {
  if (info?.visit_datetime_zero && moment(info?.visit_datetime_zero).isValid()) {
    return getDatetimeLocal(info.visit_datetime_zero, format, true, isConverYearTh);
  }
  if (info?.visitDate) {
    const datetime = `${info?.visitDate} ${info?.visitTime}`;
    if (info?.visit_timezone && !['', 'null'].includes(info?.visit_timezone)) {
      return getDatetimeLocal(moment(datetime).tz(info.visit_timezone).format(), format, false, isConverYearTh);
    }
    return getDatetimeLocal(datetime, format, false, isConverYearTh);
  }
  if (info && typeof info === 'string') {
    const datetime = `${info}`;
    if (info && !['', 'null'].includes(info)) {
      return getDatetimeLocal(moment(datetime).tz(info).format(), format, false, isConverYearTh);
    }
    return getDatetimeLocal(datetime, format, false, isConverYearTh);
  }
  return '-';
};

export const formatCurrency = (value, decimal = 2) => {
  if (Number.isNaN(value)) {
    return (0).toFixed(decimal);
  }
  return (value / 1).toFixed(decimal).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const encrypt = data => {
  const message = typeof data === 'string' ? data : JSON.stringify(data);
  return CryptoJS.AES.encrypt(message, SECRET_KEY).toString();
};

export const decrypt = (ciphertext, type = 'object') => {
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    return type === 'string' ? bytes.toString(CryptoJS.enc.Utf8) : JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    return false;
  }
};

export const randomString = (value, str = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') => {
  const shuffle = string => [...string].reduceRight((res, _, __, arr) => [...res, arr.splice(~~(Math.random() * arr.length), 1)[0]], []).join('');
  return shuffle(str).substr(0, value);
};

export const formatIDCard = str => {
  if (!str) {
    return '-';
  }
  return `${str.substring(0, 1)}-${str.substring(1, 5)}-${str.substring(5, 10)}-${str.substring(10, 12)}-${str.substring(12, 13)}`;
};

export const formatPhoneNumber = phone => {
  if (!phone) {
    return '-';
  }
  if (phone.substring(0, 3) === '+66' && phone.length === 12) {
    return `${phone.substring(0, 3)} ${phone.substring(3, 5)} ${phone.substring(5, 8)} ${phone.substring(8, 12)}`;
  }
  return phone;
};

export const getProfileName = (data, t, prefixs) => {
  const { prefix } = data || { prefix: null };
  let { firstName, lastName } = data || { firstName: '', lastName: '' };
  let label = t('Sir');
  if (prefix?.value && prefixs?.length > 0) {
    const newPrefix = prefixs.find(o => o.code === prefix.value);
    if (newPrefix?.code) {
      label = newPrefix[i18n.language] || newPrefix.en;
    }
  }
  if (data?.dataLang && typeof data?.dataLang[i18n.language] !== 'undefined') {
    if (data?.dataLang[i18n.language]?.firstName !== 'null') {
      firstName = data?.dataLang[i18n.language].firstName;
    }
    if (data?.dataLang[i18n.language]?.lastName !== 'null') {
      lastName = data?.dataLang[i18n.language].lastName;
    }
  }
  return `${label} ${firstName || ''} ${lastName || ''}`;
};

export const getDoctorNames = data => {
  const docName = {
    th: `${data?.firstName} ${data?.lastName}`
  };
  if (data?.dataLang && Object.keys(data.dataLang).length > 0) {
    Object.keys(data.dataLang).forEach(lang => {
      if (data.dataLang[lang]?.firstName !== '' && data.dataLang[lang]?.lastName !== '') {
        docName[lang] = `${data.dataLang[lang]?.firstName} ${data.dataLang[lang]?.lastName}`;
      }
    });
  }
  return docName;
};

export const getNewCountryCode = value => {
  let code = value === 'ENG' ? 'GB' : value;
  code = code === 'EN' ? 'GB' : code;
  code = code === 'Japanese' ? 'JP' : code;
  code = code === 'CHI' ? 'CN' : code;
  return code;
};

export const getNation = data => {
  if (data?.length > 0) {
    return data.map(o => i18nNation.getName(o.code, i18n.language)).join(', ');
  }
  return '-';
};

export const getPatientNation = data => {
  if (data?.length > 0) {
    return data.map(o => i18nNation.getName(o.code, i18n.language)).join(', ');
  }
  return '-';
};

export const getCareGiverName = data => {
  let name = '-';
  if (data?.careGiver !== 'null') {
    name = `${data.careGiverFirstName !== 'null' && data.careGiverFirstName} ${data.careGiverLastName !== 'null' && data.careGiverLastName}`;
  }
  return name;
};

export const validatePassportNumber = passportNumberText => {
  const regsaid = /[a-zA-Z]{2}[0-9]{7}/;
  return regsaid.test(passportNumberText);
};

export const validateThaiID = idNumber => {
  if (idNumber.substring(0, 1) === 0) {
    return false;
  }
  if (idNumber.length !== 13) {
    return false;
  }
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseFloat(idNumber.charAt(i)) * (13 - i);
  }
  if ((11 - (sum % 11)) % 10 !== parseFloat(idNumber.charAt(12))) {
    return false;
  }
  return true;
};

export const checkIdCard = passportNumber => !validateThaiID(passportNumber) && !validatePassportNumber(passportNumber);

export const getApplicationLink = data => {
  let applicationName = data?.createByAppName;
  if (data?.useWeb && data?.shortLinkForComplete && !['', 'null'].includes(data?.shortLinkForComplete)) {
    applicationName = data?.shortLinkForComplete;
  }
  return applicationName;
};

const checkNotPassTimeBooking = item => {
  const date = moment(`${item?.visitDate} ${item?.visitTime}`, 'YYYY-MM-DD HH:mm');
  return date.isValid() && moment().isSameOrBefore(date);
};

const checkPassTimeBooking = item => {
  const date = moment(`${item?.visitDate} ${item?.visitTime}`, 'YYYY-MM-DD HH:mm');
  return date.isValid() && (moment().isAfter(date) || showCallNow);
};

export const checkToConfirmBooking = item => (item?.type !== 'online' && typeof item?.information?.confirm === 'undefined' && checkNotPassTimeBooking(item?.information));

export const checkPendingInformation = item => typeof item?.pendingInformation?.visitDate !== 'undefined';

export const checkPendingByDoctor = item => checkPendingInformation(item) && (item?.pendingInformation?.role && item?.pendingInformation?.role === 'doctor');

export const checkToCallBooking = item => ((item?.information?.confirm || checkPendingInformation(item)) || checkPassTimeBooking(item.information)) && item.status === 'paid';

export const checkToCallNow = item => item?.information?.confirmCallNow || checkPassTimeBooking(item.information);

export const secToMunite = time => `${(Math.floor(time / 60)).toString().padStart(2, '0')}:${(time % 60).toString().padStart(2, '0')}`;

export const secToHms = time => `${(Math.floor(time / 60 / 60)).toString().padStart(2, '0')}:${(Math.floor((time / 60) % 60)).toString().padStart(2, '0')}:${(time % 60).toString().padStart(2, '0')}`;

export const getFileByPatCode = patCode => new Promise((resolve, reject) => {
  axios.get(`files/url/byPatCode/${patCode}`).then(response => {
    resolve(typeof response.data.fileUrl !== 'undefined' ? response.data.fileUrl : '');
  }).catch(error => {
    reject(error);
  });
});

export const getStatusLogs = (data, status = 'complete') => (data?.length > 0 && data.filter(o => o.label === status).reverse().pop()) || { createdAt: null, label: status, note: '' };

export const getCaseType = data => (data?.caseType !== 'null' && typeof data?.caseType === 'string' && data?.caseType?.toLowerCase()) || '';

export const getDoctorPrice = data => {
  let total = 0;
  const { payDoctorType, payment } = data;
  if (!data?.isDemo && payment?.status === 'successful' && (payment?.subtotal !== 'null' && payment?.subtotal !== 'NaN' && !Number.isNaN(payment.subtotal))) {
    if (data?.payDoctorAdjust && data?.payDoctorAdjust !== 'null' && !Number.isNaN(data?.payDoctorAdjust)) {
      return parseFloat(data.payDoctorAdjust);
    }
    let doctorPrice = parseFloat(payment.subtotal);
    if (data?.doctorPrice !== 'null' && !Number.isNaN(data?.doctorPrice) && parseFloat(data?.doctorPrice) > 0) {
      doctorPrice = parseFloat(data?.doctorPrice);
    } else if (data?.volunteerOnly) {
      return 0;
    }
    let payDoctor = 0;
    if (data?.payDoctor && !['', 'null'].includes(data?.payDoctor) && !Number.isNaN(data?.payDoctor)) {
      payDoctor = data?.payDoctor;
    }
    if (payDoctorType === 'rate' && parseFloat(payDoctor) > 0) {
      total = Math.ceil((parseFloat(doctorPrice) * parseFloat(payDoctor)) / 100);
    } else if (payDoctorType === 'static' && parseFloat(payDoctor) > 0) {
      total = parseFloat(payDoctor);
    } else if (getCaseType(data) === 'comercial') {
      total = Math.ceil((parseFloat(doctorPrice) * DEFAULT_DOCTOR_RATE) / 100);
    }
  }
  return total;
};

export const getCurrentAppToken = data => {
  const { patient } = data;
  const createByApp = (!['', 'null'].includes(data?.createByApp) && data?.createByApp) || (!['', 'null'].includes(patient?.appId) && patient?.appId);
  let currentToken = (!['', 'null'].includes(patient?.apnToken) && patient?.apnToken) || (!['', 'null'].includes(patient?.fcmToken) && patient?.fcmToken);
  if (data?.currentApnToken && !['', 'null'].includes(data?.currentApnToken)) {
    currentToken = data?.currentApnToken;
  }
  if (data?.currentFcmToken && !['', 'null'].includes(data?.currentFcmToken)) {
    currentToken = data?.currentFcmToken;
  }
  if (patient?.tokenByApp?.length > 0) {
    const tokenByApp = patient?.tokenByApp.find(o => o.appId === createByApp);
    if (tokenByApp?.apnToken && !['', 'null'].includes(tokenByApp?.apnToken)) {
      currentToken = tokenByApp?.apnToken;
    }
    if (tokenByApp?.fcmToken && !['', 'null'].includes(tokenByApp?.fcmToken)) {
      currentToken = tokenByApp?.fcmToken;
    }
  }
  return { currentToken, createByApp };
};

export const getDateTimeDifference = (startDateTime, endDateTime) => {
  const startTime = new Date(startDateTime).getTime();
  const endTime = new Date(endDateTime).getTime();
  const differenceInMilliseconds = endTime - startTime;
  return differenceInMilliseconds;
};

export const convertMilliseconds = milliseconds => {
  let timeMilliseconds = milliseconds;
  const oneSecondInMilliseconds = 1000;
  const oneMinuteInMilliseconds = oneSecondInMilliseconds * 60;
  const oneHourInMilliseconds = oneMinuteInMilliseconds * 60;
  const oneDayInMilliseconds = oneHourInMilliseconds * 24;
  const days = Math.floor(timeMilliseconds / oneDayInMilliseconds);
  timeMilliseconds %= oneDayInMilliseconds;
  const hours = Math.floor(timeMilliseconds / oneHourInMilliseconds);
  timeMilliseconds %= oneHourInMilliseconds;
  const minutes = Math.floor(timeMilliseconds / oneMinuteInMilliseconds);
  timeMilliseconds %= oneMinuteInMilliseconds;
  const seconds = Math.floor(timeMilliseconds / oneSecondInMilliseconds);
  return {
    days,
    hours,
    minutes,
    seconds
  };
};

export const checkAppointmentTime = booking => {
  let timeSlotRange = 15;
  if (booking?.information?.timeSlotRange) {
    timeSlotRange = booking.information.timeSlotRange;
  }
  const timeMilliseconds = timeSlotRange * 60 * 1000;
  const currentDate = moment().format('YYYY-MM-DD HH:mm:ss');
  const consultDate = moment(`${booking.information.visitDate} ${booking.information.visitTime}`).format('YYYY-MM-DD HH:mm:ss');
  const differenceInMilliseconds = getDateTimeDifference(currentDate, consultDate);
  if (differenceInMilliseconds < 0 && (differenceInMilliseconds + timeMilliseconds) > 0 && booking.status === 'paid') {
    return true;
  }
  return false;
};

export const isBrowserSupport = () => {
  const userAgent = navigator?.userAgent;
  let browserName;
  if (userAgent.match(/liff/i) || userAgent.match(/LIFF/i)) {
    browserName = 'liff';
    return { browser: browserName, support: false, agent: userAgent };
  }
  if (userAgent.match(/line/i) || userAgent.match(/Line/i) || userAgent.match(/LINE/i)) {
    browserName = 'line';
    return { browser: browserName, support: false, agent: userAgent };
  }
  if (userAgent.match(/chrome|chromium|crios/i)) {
    browserName = 'chrome';
    return { browser: browserName, support: true, agent: userAgent };
  }
  if (userAgent.match(/firefox|fxios/i)) {
    browserName = 'firefox';
    return { browser: browserName, support: true, agent: userAgent };
  }
  if (userAgent.match(/safari/i)) {
    browserName = 'safari';
    return { browser: browserName, support: true, agent: userAgent };
  }
  if (userAgent.match(/opr\//i)) {
    browserName = 'opera';
    return { browser: browserName, support: true, agent: userAgent };
  }
  if (userAgent.match(/edg/i)) {
    browserName = 'edge';
    return { browser: browserName, support: true, agent: userAgent };
  }
  browserName = 'no_defined';
  return { browser: browserName, support: false, agent: userAgent };
};

export const isAndroid = () => {
  if (/Android/i.test(navigator.userAgent)) {
    return true;
  }
  return false;
};

export const isUseApp = data => data?.currentApnToken !== '' || data?.currentFcmToken !== '';

export const getPayDoctorRate = (payDoctorRate, hospitals, hospitalLists, companies, apps) => {
  if (payDoctorRate && payDoctorRate !== 'null') return parseFloat(payDoctorRate);
  if (hospitals?.length > 0 && hospitalLists?.length > 0) {
    const hospital = hospitalLists.find(o => o.hosCode === hospitals[0]);
    if (hospital && hospital?.payDoctorRate !== 'null' && parseFloat(hospital.payDoctorRate) > 0) {
      return parseFloat(hospital.payDoctorRate);
    }
  }
  if (companies?.length > 0) {
    const company = companies[0];
    const appData = apps.find(o => o.value === company?.appForCompany?.value);
    if (appData && appData?.payDoctorRate !== 'null' && parseFloat(appData?.payDoctorRate) > 0) return parseFloat(appData?.payDoctorRate);
  }
  return PAY_DOCTOR_RATE;
};

export const displayMaskNumber = (value, mask = [3, 4, 4], prefix = '+') => {
  if (value && mask?.length > 0) {
    const newVal = value?.replace(prefix, '');
    const newRegEx = new RegExp(`^${mask.map(o => `(\\d{${o}})`).join('')}*`);
    const newOut = `${mask.map((o, index) => `$${index + 1}`).join('-')}`;
    return `${prefix}${newVal.replace(newRegEx, newOut)}`;
  }
  return '-';
};

export const getMedicalCertificate = async refCode => new Promise((resolve, reject) => {
  axios.post('/reports/pdf/consult/generate', { refCode }).then(response => {
    const { data } = response?.data || [];
    const res = data?.find(o => o?.medicalCertificate);
    resolve((res?.fileUrl !== 'null' && res?.fileUrl) || '');
  }).catch(() => {
    reject();
  });
});
